import {ComparisonTableItemTypeValueModel} from "./comparisonTableItemTypeValueModel";
import {ComparisonTableItemTypeType} from "../../definitions/types";
import {ProductAttributeTypeValueDisplayType} from "../../definitions/enums";

export class ComparisonTableItemTypeModel {
    private readonly _typeId: number;
    private readonly _typeName: string;
    private readonly _unit?: string;
    private readonly _valueDisplayMode: ProductAttributeTypeValueDisplayType;
    private readonly _values: ComparisonTableItemTypeValueModel[];

    constructor(comparisonTableItemType: ComparisonTableItemTypeType) {
        this._typeId = comparisonTableItemType.typeId;
        this._typeName = comparisonTableItemType.typeName;
        this._unit = comparisonTableItemType.unit;
        this._valueDisplayMode = comparisonTableItemType.valueDisplayMode ?? ProductAttributeTypeValueDisplayType.Default;
        this._values = comparisonTableItemType.values
            .filter((value): value is { valueId: number, valueName: string } => value !== null)
            .map((value) => new ComparisonTableItemTypeValueModel(value));
    }

    /**
     * @property typeId
     * @description The unique identifier of the specification type.
     * @returns {number}
     */
    public get typeId(): number {
        return this._typeId;
    }

    /**
     * @property typeName
     * @description The name of the specification type.
     * @returns {string}
     */
    public get typeName(): string {
        return this._typeName;
    }

    /**
     * @property unit
     * @description The unit of the specification type.
     * @returns {string | undefined}
     */
    public get unit(): string | undefined {
        return this._unit;
    }

    /**
     * @property valueDisplayMode
     * @description The value display mode of the specification type.
     * @returns {ProductAttributeTypeValueDisplayType}
     */
    public get valueDisplayMode(): ProductAttributeTypeValueDisplayType {
        return this._valueDisplayMode;
    }

    /**
     * @property values
     * @description The values of the specification type.
     * @returns {ComparisonTableItemTypeValueModel[]}
     */
    public get values(): ComparisonTableItemTypeValueModel[] {
        return this._values;
    }

    /**
     * @property hasValues
     * @description Whether the specification type has values.
     * @returns {boolean}
     */
    public get hasValues(): boolean {
        return this._values.length > 0;
    }

    /**
     * @property valuesCount
     * @description The number of values of the specification type.
     * @returns {number}
     */
    public get valuesCount(): number {
        return this._values.length;
    }

    /**
     * @property value
     * @description The comma separated values of the specification type.
     * @returns {string}
     * @example "Red, Blue, Green" or "---" if no values
     */
    public get value(): string {
        return this.hasValues
            ? this._values.map((value) => `${value.valueName}${this._unit ? this._unit : ''}`).join(', ')
            : '---';
    }
}